import React, { useState, useRef } from "react";
import {
  TextField,
  Button,
  Container,
  Typography,
  Grid,
  Paper,
  CircularProgress,
} from "@mui/material";
import { Autocomplete } from "@react-google-maps/api"; // Import Autocomplete from @react-google-maps/api

interface SignupProps {
  email: string;
  password: string;
  name: string;
  companyName: string;
  phoneNumber: string;
  address: string;
  signedTermsAndConditions: boolean;
  setEmail: (value: string) => void;
  setPassword: (value: string) => void;
  setName: (value: string) => void;
  setCompanyName: (value: string) => void;
  setPhoneNumber: (value: string) => void;
  setAddress: (value: string) => void;
  signup: () => Promise<void>;
  setSignedTermsAndConditions: (value: boolean) => void;
}

const Signup: React.FC<SignupProps> = ({
  email,
  password,
  name,
  companyName,
  phoneNumber,
  address,
  signedTermsAndConditions,
  setEmail,
  setPassword,
  setName,
  setCompanyName,
  setPhoneNumber,
  setAddress,
  signup,
  setSignedTermsAndConditions,
}) => {
  const [emailError, setEmailError] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const validateEmail = (email: string) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    setEmailError(emailRegex.test(email) ? null : "Invalid email format");
    return emailRegex.test(email);
  };

  async function signupChecks() {
    if (isLoading) return; // Prevent multiple clicks

    const emailValid = validateEmail(email);
    const passwordValid = validatePassword();
    const termsValid = signedTermsAndConditions;

    if (emailValid && passwordValid && termsValid) {
      setIsLoading(true);
      await signup();
      setIsLoading(false);
    } else if (!termsValid) {
      alert("You must accept the terms and conditions to sign up.");
    }
  }

  function getAppUrl() {
    const url = window.location.href;
    return url;
  }

  const termsUrl = getAppUrl() + "/termsandconditions.html";

  const validatePassword = () => {
    const isValid =
      password.length >= 8 &&
      /\d/.test(password) &&
      /[!@#$%^&*]/.test(password);
    console.log(isValid);
    setPasswordError(
      isValid
        ? null
        : "Password must be at least 8 characters long and include a number and a special character"
    );
    return isValid;
  };

  const autocompleteRef = useRef<google.maps.places.Autocomplete | null>(null);

  const handlePlaceChanged = () => {
    if (autocompleteRef.current) {
      const place = autocompleteRef.current.getPlace();
      if (place.formatted_address) {
        setAddress(place.formatted_address);
      }
    }
  };

  return (
    <Container maxWidth="sm">
      <Paper elevation={3} style={{ padding: "20px" }}>
        <Typography variant="h5" gutterBottom>
          Sign Up to our waiting list
        </Typography>
        <Typography variant="h6" gutterBottom>
          and view our product <b>demo</b> now!
        </Typography>
        <Grid container spacing={0}>
          <Grid item xs={12}>
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
              }}
              error={!!emailError}
              helperText={emailError}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              error={!!passwordError}
              helperText={passwordError}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Company Name"
              type="text"
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              label="Phone Number"
              type="text"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              fullWidth
              margin="normal"
            />
          </Grid>
          <Grid item xs={12}>
            <Autocomplete
              onLoad={(autocomplete) =>
                (autocompleteRef.current = autocomplete)
              }
              onPlaceChanged={handlePlaceChanged}
            >
              <TextField
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                margin="normal"
              />
            </Autocomplete>
          </Grid>
          <Grid item xs={12}>
            <div
              style={{ display: "flex", alignItems: "center", padding: "10px" }}
            >
              <input
                type="checkbox"
                checked={signedTermsAndConditions}
                onChange={(e) => setSignedTermsAndConditions(e.target.checked)}
              />
              <Typography variant="body2" style={{ marginLeft: "8px" }}>
                I accept the{" "}
                <a href={termsUrl} target="_blank" rel="noopener noreferrer">
                  terms and conditions
                </a>
              </Typography>
            </div>
          </Grid>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              onClick={signupChecks}
              fullWidth
              disabled={isLoading} // Disable button when loading
              startIcon={isLoading ? <CircularProgress size={20} /> : null} // Add spinner
            >
              {isLoading ? "Signing Up..." : "Sign Up"}{" "}
              {/* Show loading text */}
            </Button>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default Signup;
